import React, { useState } from 'react'
import { User, Briefcase, Building2, Award } from 'lucide-react'
import * as api from '../../../app/axios/api'
import { toast } from 'sonner'

export default function EmployeeForm() {
  const [formData, setFormData] = useState({
    employee_name: '',
    employee_id: '',
    department: '',
    position_title: '',
  })

  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.employee_name.trim()) {
      newErrors.employee_name = 'Employee name is required'
    }

    if (!formData.employee_id.trim()) {
      newErrors.employee_id = 'Employee ID is required'
    }

    if (!formData.department.trim()) {
      newErrors.department = 'Department is required'
    }

    if (!formData.position_title.trim()) {
      newErrors.position_title = 'Position title is required'
    }

    return newErrors
  }

  const handleSubmit = async () => {
    const newErrors = validate()

    if (Object.keys(newErrors).length === 0) {
      setSubmitted(true)

      try {
        const res = await api.createEmployee(formData)
        toast.success('Followup Created Successfully')
      } catch (error) {
        console.error(error)
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          toast.error(`Error: ${error.response.data.message}`)
        }
      }

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          employee_name: '',
          employee_id: '',
          department: '',
          position_title: '',
        })
        setSubmitted(false)
      }, 3000)
    } else {
      setErrors(newErrors)
    }
  }

  const handleReset = () => {
    setFormData({
      employee_name: '',
      employee_id: '',
      department: '',
      position_title: '',
    })
    setErrors({})
    setSubmitted(false)
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-2xl mx-auto'>
        <div className='bg-white rounded-2xl shadow-xl overflow-hidden'>
          {/* Header */}
          <div className='bg-gradient-to-r from-blue-600 to-purple-600 px-6 sm:px-8 py-8 sm:py-10'>
            <div className='flex items-center justify-center mb-3'>
              <User className='w-10 h-10 sm:w-12 sm:h-12 text-white' />
            </div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white text-center'>
              Employee Registration
            </h1>
            <p className='text-blue-100 text-center mt-2 text-sm sm:text-base'>
              Fill in the details to create a new employee record
            </p>
          </div>

          {/* Success Message */}
          {submitted && (
            <div className='mx-4 sm:mx-8 mt-6 bg-green-50 border-l-4 border-green-500 p-4 rounded'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-green-500'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-green-800'>
                    Employee registered successfully!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <div className='px-4 sm:px-8 py-8 space-y-6'>
            {/* Employee Name */}
            <div>
              <label
                htmlFor='employee_name'
                className='flex items-center text-sm font-semibold text-gray-700 mb-2'
              >
                <User className='w-4 h-4 mr-2 text-blue-600' />
                Employee Name
              </label>
              <input
                type='text'
                id='employee_name'
                name='employee_name'
                value={formData.employee_name}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${
                  errors.employee_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder='Enter full name'
              />
              {errors.employee_name && (
                <p className='mt-1 text-sm text-red-600'>
                  {errors.employee_name}
                </p>
              )}
            </div>

            {/* Employee ID */}
            <div>
              <label
                htmlFor='employee_id'
                className='flex items-center text-sm font-semibold text-gray-700 mb-2'
              >
                <Briefcase className='w-4 h-4 mr-2 text-blue-600' />
                Employee ID
              </label>
              <input
                type='text'
                id='employee_id'
                name='employee_id'
                value={formData.employee_id}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${
                  errors.employee_id ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder='e.g., EMP001'
              />
              {errors.employee_id && (
                <p className='mt-1 text-sm text-red-600'>
                  {errors.employee_id}
                </p>
              )}
            </div>

            {/* Department */}
            <div>
              <label
                htmlFor='department'
                className='flex items-center text-sm font-semibold text-gray-700 mb-2'
              >
                <Building2 className='w-4 h-4 mr-2 text-blue-600' />
                Department
              </label>
              <select
                id='department'
                name='department'
                value={formData.department}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${
                  errors.department ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value=''>Select department</option>
                <option value='software development'>
                  software development
                </option>
                <option value='Information Technology'>
                  Information Technology
                </option>

                <option value='Marketing'>Marketing</option>
              </select>
              {errors.department && (
                <p className='mt-1 text-sm text-red-600'>{errors.department}</p>
              )}
            </div>

            {/* Position Title */}
            <div>
              <label
                htmlFor='position_title'
                className='flex items-center text-sm font-semibold text-gray-700 mb-2'
              >
                <Award className='w-4 h-4 mr-2 text-blue-600' />
                Position Title
              </label>
              <input
                type='text'
                id='position_title'
                name='position_title'
                value={formData.position_title}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${
                  errors.position_title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder='e.g., Senior Developer'
              />
              {errors.position_title && (
                <p className='mt-1 text-sm text-red-600'>
                  {errors.position_title}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className='flex flex-col sm:flex-row gap-3 pt-4'>
              <button
                onClick={handleSubmit}
                className='flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              >
                Register Employee
              </button>
              <button
                onClick={handleReset}
                className='flex-1 sm:flex-initial bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-all'
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
